import { isDefined } from "@omni/utils/helper.utils";
import { memoize, throttle } from "lodash-es";
import { memo, useCallback, useMemo, useRef } from "react";
import { FieldValues } from "react-hook-form";

import { AutocompleteAddress } from "@/services/bpost.service";

import {
    BPostAddressFieldComponent,
    BPostAddressFieldComponentProps,
    SetOptionsCallback
} from "./bPostAddressField.component";
import { autocomplete } from "./helpers/autocomplete.helpers";
import { getBoxAddresses, getBoxSelectProps } from "./helpers/box.helpers";

type BPostAddressFieldProperties = {
    onTimeout: () => void;
};

type BPostAddressFieldProps<T extends FieldValues> = Omit<
    BPostAddressFieldComponentProps<T>,
    "loadOptions"
> &
    BPostAddressFieldProperties;

const BPostAddressFieldContainer = <T extends FieldValues>({
    onTimeout,
    ...componentBaseProps
}: BPostAddressFieldProps<T>) => {
    const autocompete = useMemo(() => autocomplete(onTimeout), [onTimeout]);

    const setOptionsCallbackRef = useRef<SetOptionsCallback | undefined>(
        undefined
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const getOptions = useCallback(
        memoize(async (inputValue: string) => {
            if (!inputValue?.length) return [];

            return autocompete(inputValue).catch(() => []);
        }),
        [autocompete]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const updateOptions = useCallback(
        throttle(async (inputValue: string) => {
            const setOptions = setOptionsCallbackRef.current;

            if (!isDefined(setOptions)) return;

            getOptions(inputValue).then(setOptions);
        }, 250),
        [getOptions]
    );

    const loadOptions = async (
        inputValue: string,
        currentAddress: AutocompleteAddress | null,
        callback: SetOptionsCallback
    ) => {
        const { isBoxSelect, box, boxType } = getBoxSelectProps({
            inputValue: inputValue,
            address: currentAddress
        });

        if (
            currentAddress?.listOfBoxes &&
            currentAddress?.houseNumber &&
            isBoxSelect
        ) {
            const boxAddresses = getBoxAddresses({
                address: currentAddress,
                boxType: boxType,
                box: box
            });

            callback(boxAddresses);
        } else if (getOptions.cache.has(inputValue)) {
            getOptions(inputValue).then(callback);
        } else {
            setOptionsCallbackRef.current = callback;
            updateOptions(inputValue);
        }
    };

    return (
        <BPostAddressFieldComponent<T>
            {...componentBaseProps}
            loadOptions={loadOptions}
        />
    );
};

export const BPostAddressField = memo(
    BPostAddressFieldContainer
) as typeof BPostAddressFieldContainer;
